import type { FileMeta } from "../shared/protocol";

export interface AuthConfig {
  salt: string;
  iterations: number;
  algorithm: "PBKDF2-SHA-256";
  digest: "SHA-256";
  verifierBytes: number;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryAfter?: number;

  constructor(status: number, message: string, code = "error", retryAfter?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
  }
}

let cachedConfig: AuthConfig | null = null;

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function errorFrom(response: Response, data: unknown): ApiError {
  const record = typeof data === "object" && data !== null ? (data as Record<string, unknown>) : {};
  const message =
    typeof record.message === "string"
      ? record.message
      : response.status === 401
        ? "Authentication required."
        : "Something went wrong.";
  const code = typeof record.error === "string" ? record.error : "error";
  const retryAfter =
    typeof record.retryAfter === "number" ? record.retryAfter : undefined;
  return new ApiError(response.status, message, code, retryAfter);
}

export async function getAuthConfig(): Promise<AuthConfig> {
  if (cachedConfig) return cachedConfig;
  const response = await fetch("/api/auth/config", {
    credentials: "same-origin",
    cache: "no-store",
  });
  const data = await readJson(response);
  if (!response.ok) throw errorFrom(response, data);
  cachedConfig = data as AuthConfig;
  return cachedConfig;
}

function base64UrlToBytes(value: string): Uint8Array {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export async function deriveVerifier(
  password: string,
  config: AuthConfig,
): Promise<string> {
  if (!crypto.subtle) {
    throw new ApiError(0, "This browser does not support secure password checks.");
  }
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(password),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      salt: base64UrlToBytes(config.salt) as unknown as BufferSource,
      iterations: config.iterations,
      hash: config.digest,
    },
    key,
    config.verifierBytes * 8,
  );
  return bytesToBase64Url(new Uint8Array(bits));
}

export async function login(password: string): Promise<void> {
  const config = await getAuthConfig();
  const verifier = await deriveVerifier(password, config);
  const response = await fetch("/api/auth/login", {
    method: "POST",
    credentials: "same-origin",
    cache: "no-store",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ verifier }),
  });
  const data = await readJson(response);
  if (!response.ok) throw errorFrom(response, data);
}

export async function sessionStatus(): Promise<{
  authenticated: boolean;
  expiresAt: number | null;
}> {
  const response = await fetch("/api/auth/status", {
    credentials: "same-origin",
    cache: "no-store",
  });
  const data = await readJson(response);
  if (!response.ok) throw errorFrom(response, data);
  return data as { authenticated: boolean; expiresAt: number | null };
}

export async function logout(): Promise<void> {
  try {
    await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
    });
  } catch {
    // The local state is cleared regardless.
  }
}

export interface UploadHandle {
  abort: () => void;
}

export function uploadFile(
  file: File,
  onProgress: (percent: number) => void,
  onDone: (file: FileMeta) => void,
  onError: (error: ApiError) => void,
): UploadHandle {
  const request = new XMLHttpRequest();
  const form = new FormData();
  form.append("file", file);

  request.open("POST", "/api/files", true);
  request.withCredentials = true;
  request.responseType = "text";

  request.upload.onprogress = (event) => {
    if (event.lengthComputable) {
      onProgress(Math.round((event.loaded / event.total) * 100));
    }
  };

  request.onload = () => {
    let data: unknown = null;
    try {
      data = JSON.parse(request.responseText) as unknown;
    } catch {
      data = null;
    }

    if (request.status >= 200 && request.status < 300) {
      const record = data as { file?: FileMeta } | null;
      if (record?.file) {
        onDone(record.file);
        return;
      }
    }

    const record = typeof data === "object" && data !== null ? (data as Record<string, unknown>) : {};
    const message =
      typeof record.message === "string" ? record.message : "Upload failed.";
    const code = typeof record.error === "string" ? record.error : "upload_failed";
    onError(new ApiError(request.status, message, code));
  };

  request.onerror = () => onError(new ApiError(0, "Could not upload that file."));
  request.onabort = () => onError(new ApiError(0, "Upload cancelled.", "cancelled"));

  request.send(form);

  return {
    abort: () => request.abort(),
  };
}

export async function deleteRemoteFile(id: string): Promise<void> {
  try {
    await fetch(`/api/files/${encodeURIComponent(id)}`, {
      method: "DELETE",
      credentials: "same-origin",
      cache: "no-store",
    });
  } catch {
    // Best effort.
  }
}
